import ButtonWithLoader from "@/common/components/ui/loading/ButtonLoading";
import { getCode } from "@/lib/utils";
import { useGetAllPurchaseRequestors } from "@/modules/invoice/hook";
import {
  PURCHASEORDER_STATUS,
  emptyPurchaseOrder,
} from "@/types/purchaseOrder.types";
import { IPurchaseRequisition } from "@/types/purchaseRequisition.type";
import {
  Button,
  Card,
  Modal,
  Label,
  Select,
  FileInput,
  Checkbox,
} from "flowbite-react";
import { useUser } from "@/common/services/users.api";
import { useCompanyStore } from "@/common/states/store/Company";
import { useGetAllSuppliers } from "@/common/hooks";
import React, { useState } from "react";
import { useToastContext } from "@/common/components/ui/toast/toast.context";
import { createPurchaseOrder } from "@/common/services/purchaseOrder.api";
import { ApprovedRow } from "./ModalValidatePurchaseRequisition";

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const ModalGenerateOrder: React.FC<{
  requisition: IPurchaseRequisition;
  onClose: () => void;
}> = ({ requisition, onClose }) => {
  const { data: requestors = [] } = useGetAllPurchaseRequestors();
  const { data: suppliers = [] } = useGetAllSuppliers();
  const user = useUser();
  const companyId = useCompanyStore((state) => state?.company?.companyId);
  const [loading, setLoading] = useState(false);
  const { notifyError, notifySuccess } = useToastContext();

  const [draftOrders, setDraftOrders] = useState<
    {
      supplierId: string;
      items: Record<string, number>;
      nfFiles: File | null;
      billetFiles: File | null;
    }[]
  >([{ supplierId: "", items: {}, nfFiles: null, billetFiles: null }]);

  const handleSupplierChange = (orderIndex: number, supplierId: string) => {
    setDraftOrders((prev) =>
      prev.map((draft, index) =>
        index === orderIndex ? { ...draft, supplierId } : draft
      )
    );
  };

  const handleFileChange = (
    orderIndex: number,
    fileType: "nfFiles" | "billetFiles",
    files: FileList | null
  ) => {
    const file = files?.[0] || null;
    setDraftOrders((prev) =>
      prev.map((draft, index) => {
        if (index === orderIndex) {
          return { ...draft, [fileType]: file };
        }
        return draft;
      })
    );
  };

  const addDraftOrder = () => {
    setDraftOrders([
      ...draftOrders,
      { supplierId: "", items: {}, nfFiles: null, billetFiles: null },
    ]);
  };

  const removeDraftOrder = (orderIndex: number) => {
    const newDrafts = draftOrders.filter((_, i) => i !== orderIndex);
    setDraftOrders(newDrafts);
  };

  const toggleItemInOrder = (
    orderIndex: number,
    productId: string,
    checked: boolean
  ) => {
    setDraftOrders((prev) => {
      const newDrafts = JSON.parse(JSON.stringify(prev));

      if (checked) {
        newDrafts.forEach((draft, index) => {
          if (index !== orderIndex) {
            delete draft.items[productId];
          }
        });

        const requisitionItem = requisition.itens.find(
          (i) => i.productId === productId
        );
        if (requisitionItem) {
          newDrafts[orderIndex].items[productId] = requisitionItem.quantity;
        }
      } else {
        delete newDrafts[orderIndex].items[productId];
      }

      return newDrafts;
    });
  };

  const onSave = async () => {
    setLoading(true);
    try {
      const requestor = requestors.find(
        (req) => req.employeeEmail === user.email
      );

      if (!requestor) {
        notifyError("Usuário solicitante não encontrado.");
        setLoading(false);
        return;
      }

      const validDrafts = draftOrders.filter(
        (draft) =>
          draft.supplierId &&
          draft.nfFiles &&
          draft.billetFiles &&
          Object.keys(draft.items).length > 0
      );

      if (validDrafts.length === 0) {
        notifyError(
          "Adicione itens, anexo de NF, anexo de boleto e selecione um fornecedor para gerar uma ordem de compra."
        );
        setLoading(false);
        return;
      }

      for (const draft of validDrafts) {
        const supplier = suppliers.find((s) => s._id === draft.supplierId);
        if (!supplier) continue;

        let nfFiles = "";
        if (draft.nfFiles) {
          try {
            nfFiles = await fileToBase64(draft.nfFiles);
          } catch (error) {
            console.error("Error converting nfFiles to base64:", error);
            notifyError(
              `Erro ao processar a nota fiscal para ${supplier.companyName}.`
            );
            continue;
          }
        }

        let billetFiles = "";
        if (draft.billetFiles) {
          try {
            billetFiles = await fileToBase64(draft.billetFiles);
          } catch (error) {
            console.error("Error converting billetFile to base64:", error);
            notifyError(
              `Erro ao processar o boleto para ${supplier.companyName}.`
            );
            continue;
          }
        }

        const code = getCode();
        const defaultValues = emptyPurchaseOrder();
        const now = new Date();

        const poItems = Object.keys(draft.items).map((itemId) => {
          const requisitionItem = requisition.itens.find(
            (i) => i.productId === itemId
          )!;
          const quantity = draft.items[itemId];
          return {
            description: requisitionItem.productName,
            qtd: quantity,
            value: (requisitionItem as unknown as ApprovedRow).quoteValue || 0,
            productId: requisitionItem.productId,
            productName: requisitionItem.productName,
          };
        });

        const totalValue = poItems.reduce((acc, item) => {
          const value = Number(item.value) || 0;
          const qtd = Number(item.qtd) || 0;
          return acc + value * qtd;
        }, 0);

        const CATEGORY_ORDER = {
          id: "49220f39-89d5-4daa-a95a-d20b2d3a3c08",
          name: "Compra de Peças	",
        };

        const COST_CENTER_OPERATIONAL = {
          id: "70d90ad9-9ca2-4186-a016-e36b2efa7eb3",
          name: "OPERAÇÃO",
        };

        const purchaseOrder = {
          ...defaultValues,
          description: `Ordem de compra gerada a partir da requisição ${requisition.code}`,
          companyId: companyId || "",
          requestorId: requestor._id,
          requestorName: requestor.name,
          requestorEmail: requestor.employeeEmail,
          code: code.toString(),
          status: PURCHASEORDER_STATUS.PENDING,
          supplierId: supplier._id,
          supplierName: supplier.companyName,
          financial: {
            ...defaultValues.financial,
            categoryId: CATEGORY_ORDER.id,
            categoryName: CATEGORY_ORDER.name,
            costCenterId: COST_CENTER_OPERATIONAL.id,
            costCenterName: COST_CENTER_OPERATIONAL.name,
            value: totalValue,
            expectedDate: now,
            nfFiles,
            billetFiles,
            instalment: [
              {
                installmentNumber: "1",
                value: totalValue,
                dueDate: now,
              },
            ],
          },
          itens: poItems,
          createdAt: now,
        };

        const result = await createPurchaseOrder(purchaseOrder);
        if (result.ok) {
          notifySuccess(
            `Ordem de compra para ${supplier.companyName} gerada com sucesso.`
          );
        } else {
          notifyError(
            result.message ||
              `Erro ao gerar ordem de compra para ${supplier.companyName}`
          );
        }
      }
      onClose();
    } finally {
      setLoading(false);
    }
  };

  const allAssignedProductIds = new Set(
    draftOrders.flatMap((d) => Object.keys(d.items))
  );

  return (
    <Modal show={true} onClose={onClose} size="2xl">
      <Modal.Header>
        <h2 className="text-black">Gerar ordem de compra</h2>
      </Modal.Header>
      <Modal.Body>
        {draftOrders.map((order, orderIndex) => {
          return (
            <Card key={orderIndex} className="mb-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg">
                  Ordem de Compra #{orderIndex + 1}
                </h3>
                {draftOrders.length > 1 && (
                  <Button
                    color="failure"
                    size="xs"
                    onClick={() => removeDraftOrder(orderIndex)}
                  >
                    Remover
                  </Button>
                )}
              </div>

              <div className="flex flex-col gap-2 ">
                <Label
                  color={"black"}
                  htmlFor={`supplier-select-${orderIndex}`}
                >
                  Fornecedor
                </Label>
                <Select
                  id={`supplier-select-${orderIndex}`}
                  value={order.supplierId}
                  color={"black"}
                  onChange={(e) =>
                    handleSupplierChange(orderIndex, e.target.value)
                  }
                >
                  <option value="">Selecione um fornecedor</option>
                  {suppliers.map((supplier) => (
                    <option key={supplier._id} value={supplier._id}>
                      {supplier.companyName}
                    </option>
                  ))}
                </Select>
              </div>

              <p className="mt-3 font-semibold">Itens:</p>
              <div className="flex flex-col gap-1">
                {requisition.itens.map((item) => {
                  const isChecked = order.items[item.productId] !== undefined;
                  const isDisabled =
                    !isChecked && allAssignedProductIds.has(item.productId);

                  return (
                    <div
                      key={item.productId}
                      className="grid grid-cols-2 items-center gap-4 py-2 border-t first:border-t-0"
                    >
                      <div className="flex items-center text-black gap-2">
                        <Checkbox
                          id={`item-check-${orderIndex}-${item.productId}`}
                          checked={isChecked}
                          disabled={isDisabled}
                          onChange={(e) =>
                            toggleItemInOrder(
                              orderIndex,
                              item.productId,
                              e.target.checked
                            )
                          }
                        />
                        <Label
                          color={"black"}
                          htmlFor={`item-check-${orderIndex}-${item.productId}`}
                          className={`${isDisabled ? "text-gray-400" : ""}`}
                        >
                          {item.productName}
                        </Label>
                      </div>
                      <div>
                        <p
                          className={`text-sm ${
                            isDisabled ? "text-gray-400" : "text-gray-500"
                          }`}
                        >
                          Qtd: {item.quantity}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-4 grid grid-cols-2 gap-4">
                <div>
                  <Label
                    htmlFor={`nf-file-upload-${orderIndex}`}
                    value="Anexar Nota Fiscal"
                  />
                  <FileInput
                    required
                    id={`nf-file-upload-${orderIndex}`}
                    helperText="Anexe a nota fiscal"
                    onChange={(e) =>
                      handleFileChange(orderIndex, "nfFiles", e.target.files)
                    }
                  />
                </div>
                <div>
                  <Label
                    htmlFor={`billet-file-upload-${orderIndex}`}
                    value="Anexar Boleto"
                  />
                  <FileInput
                    required
                    id={`billet-file-upload-${orderIndex}`}
                    helperText="Anexe o boleto"
                    onChange={(e) =>
                      handleFileChange(
                        orderIndex,
                        "billetFiles",
                        e.target.files
                      )
                    }
                  />
                </div>
              </div>
            </Card>
          );
        })}
        <Button onClick={addDraftOrder} color="red" size="sm">
          Adicionar outra Ordem de Compra
        </Button>
      </Modal.Body>
      <Modal.Footer>
        <Button color="gray" onClick={onClose}>
          Fechar
        </Button>
        <ButtonWithLoader onClick={onSave} disabled={loading}>
          {loading ? "Processando..." : "Gerar ordens de compra"}
        </ButtonWithLoader>
      </Modal.Footer>
    </Modal>
  );
};
